import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import {
    LogisticsActivity,
    LogisticsIntensity,
    LogisticsIntensityBreakdown,
    LogisticsSiteMethod,
    ShippingMethod,
} from '@lune-climate/lune'
import { Text } from '@lune-fe/lune-ui-lib'
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined'
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import OilBarrelOutlinedIcon from '@mui/icons-material/OilBarrelOutlined'
import { Big } from 'big.js'

import { formatNumbers, KG_PER_LB } from '../../utils/utils'

import { isAirTransportMethod } from './utils/logisticsUtils'
import ResultLabeledSectionAndTiles from './ResultLabeledSectionAndTiles'

function formatIntensity({ amount, unit }: LogisticsIntensity): number | string {
    function _format(amount: string) {
        switch (unit) {
            case LogisticsIntensity.unit.T_PER_T:
            case LogisticsIntensity.unit.T_PER_TKM:
                return Big(amount).mul(1_000_000)

            case LogisticsIntensity.unit.KG_PER_T:
            case LogisticsIntensity.unit.KG_PER_TKM:
                return Big(amount).mul(1_000)

            case LogisticsIntensity.unit.G_PER_T:
            case LogisticsIntensity.unit.G_PER_TKM:
                return Big(amount)

            case LogisticsIntensity.unit.MG_PER_T:
            case LogisticsIntensity.unit.MG_PER_TKM:
                return Big(amount).div(1_000)

            case LogisticsIntensity.unit.LBS_PER_T:
            case LogisticsIntensity.unit.LBS_PER_TKM:
                return Big(amount).mul(KG_PER_LB).mul(1_000)
        }
    }

    return formatNumbers(_format(amount).toString())
}

// We show intensity in gCO2e
function formatIntensityUnit(unit: LogisticsIntensity.unit): string {
    switch (unit) {
        case LogisticsIntensity.unit.T_PER_T:
        case LogisticsIntensity.unit.KG_PER_T:
        case LogisticsIntensity.unit.G_PER_T:
        case LogisticsIntensity.unit.MG_PER_T:
        case LogisticsIntensity.unit.LBS_PER_T:
            return 'gCO₂e/t'
        case LogisticsIntensity.unit.T_PER_TKM:
        case LogisticsIntensity.unit.KG_PER_TKM:
        case LogisticsIntensity.unit.G_PER_TKM:
        case LogisticsIntensity.unit.MG_PER_TKM:
        case LogisticsIntensity.unit.LBS_PER_TKM:
            return 'gCO₂e/t*km'
    }
}

function getDistanceMethodText(method?: ShippingMethod | LogisticsSiteMethod): string {
    if (!method) {
        return ''
    }

    if (isAirTransportMethod(method)) {
        return ` ${t`(using great circle distance)`}`
    }

    return `${t`(using shortest feasible distance)`}`
}

export const LogisticsAdditionalDetails = ({
    activity,
    intensity,
    method,
}: {
    activity: LogisticsActivity | null
    intensity: LogisticsIntensityBreakdown | null
    method?: ShippingMethod | LogisticsSiteMethod
}) => {
    useLingui()

    return (
        <>
            <Text variant={'h5'}>{t`Additional details`}</Text>
            <Text variant="body3" sx={{ color: `Grey600`, display: 'block', mt: 3, mb: 4 }}>
                {t`All emissions are reported on a Well-To-Wheel basis unless otherwise stated.`}
            </Text>
            <ResultLabeledSectionAndTiles
                tiles={[
                    ...(activity
                        ? [
                              {
                                  icon: <Inventory2OutlinedIcon />,
                                  label: t`Transport activity: ${formatNumbers(activity.value, 2)} ${activity.unit}${getDistanceMethodText(method)}`,
                              },
                          ]
                        : []),
                    ...(intensity
                        ? [
                              {
                                  icon: <BarChartOutlinedIcon />,
                                  label: t`Carbon intensity: ${formatIntensity(intensity.wtw)} ${formatIntensityUnit(intensity.wtw.unit)}`,
                              },
                              {
                                  icon: <OilBarrelOutlinedIcon />,
                                  label: t`Carbon intensity (WTT): ${formatIntensity(intensity.wtt)} ${formatIntensityUnit(intensity.wtt.unit)}`,
                              },
                              {
                                  icon: <CloudOutlinedIcon />,
                                  label: t`Carbon intensity (TTW): ${formatIntensity(intensity.ttw)} ${formatIntensityUnit(intensity.ttw.unit)}`,
                              },
                          ]
                        : []),
                ]}
            />
        </>
    )
}

export default LogisticsAdditionalDetails
